// Kun chegarasi — TENANT zonasida, brauzernikida EMAS.
//
// =====================================================================
// ⚠️ NEGA BU FAYL BOR VA NEGA AYNAN SHU YERDA
// =====================================================================
// «Bugun qancha pul oldim» degan savolning butun ma'nosi «bugun» so'zida.
// `dayjs().startOf('day').toISOString()` BRAUZER zonasining yarim tunini
// beradi; server esa davrni tenantning `day_timezone` ida hisoblaydi
// (ADR-015). Staging tenanti `Asia/Kabul` (UTC+04:30), ish esa
// `Asia/Tashkent` (UTC+05:00) dan olib borilyapti — kuniga YARIM SOAT
// davomida brauzer allaqachon ertangi kunda.
//
// ⚠️ VA BU JIMGINA BO'LARDI: so'rov 200 qaytaradi, kartochkalar to'ladi,
// xato jurnalida hech narsa yo'q — faqat SON boshqacha. Aynan shu nuqson
// admin panelida o'lchangan va u yerda `shared/lib/dayRange.ts` bilan
// yopilgan.
//
// ⚠️ NEGA ADMIN'DAGISI IMPORT QILINMADI: `frontend/partner` alohida
// Docker konteksti va `../admin` ga import image ichida umuman
// topilmaydi (`tests/guards.mjs` §8 buni qo'riqlaydi). Bu paneldagi
// `shared/lib/format.ts` da esa zona qismi bor, KUN CHEGARASI yo'q.
// Shuning uchun kerak bo'lgan uchta funksiya yagona iste'molchisi bilan
// yonma-yon turadi; ikkinchi iste'molchi paydo bo'lganda joyi —
// `shared/lib/format.ts`.

import { getDisplayTimeZone } from '@/shared/lib/format'

/** `YYYY-MM-DD` → `[yil, oy, kun]`; shakli buzuq bo'lsa `null` */
function dayParts(day: string): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim())
  if (!match) return null
  return [Number(match[1]), Number(match[2]), Number(match[3])]
}

/**
 * Zonaning `at` onidagi siljishi, daqiqada.
 *
 * `Intl` ning devor-soatini UTC bilan solishtiradi — yozgi vaqt va
 * yarim soatli zonalar (Kobul +04:30) shu yo'l bilan o'zidan to'g'ri
 * chiqadi. Xatoda `0` — vaqt YO'QOLGANDAN ko'ra UTC ko'rsatilgani afzal.
 */
function zoneOffsetMinutes(zone: string, at: Date): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(at)
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0')
    // ⚠️ `hour12: false` ba'zi muhitlarda yarim tunni "24" deb beradi
    const wallUtc = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour') % 24,
      get('minute'),
      get('second'),
    )
    // `at` dagi millisekundlar `wallUtc` da yo'q — solishtirish sekundda
    return Math.round((wallUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000)
  } catch {
    return 0
  }
}

/** TENANT zonasidagi bugungi kalendar kuni, `YYYY-MM-DD` */
export function tenantToday(zone: string = getDisplayTimeZone()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date())
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
    return `${get('year')}-${get('month')}-${get('day')}`
  } catch {
    return new Date().toISOString().slice(0, 10)
  }
}

/**
 * Kalendar kunini `delta` kun surish.
 *
 * ⚠️ Sof KALENDAR arifmetikasi — zona bu yerga kirmaydi: «ertaga» har
 * qanday zonada bir kun keyingi sana. Zona faqat o'sha kun QAYSI ONDA
 * boshlanishini aniqlaydi ({@link startOfDayIso}).
 */
export function shiftDay(day: string, delta: number): string {
  const parts = dayParts(day)
  if (!parts) return day
  const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]))
  d.setUTCDate(d.getUTCDate() + delta)
  return d.toISOString().slice(0, 10)
}

function nextDay(day: string): string {
  return shiftDay(day, 1)
}

/** Shu kun tegishli oyning birinchi sanasi */
export function monthStart(day: string): string {
  const parts = dayParts(day)
  if (!parts) return day
  return `${String(parts[0]).padStart(4, '0')}-${String(parts[1]).padStart(2, '0')}-01`
}

/**
 * `YYYY-MM-DD` (tenant kuni) → o'sha kun BOSHLANGAN on, UTC ISO.
 * Kobulda `2026-08-18` → `2026-08-17T19:30:00.000Z`.
 *
 * ⚠️ IKKI QADAM: siljish qaysi ondaligiga bog'liq, biz esa hali o'sha
 * onni bilmaymiz. Birinchi taxmin devor-soati bo'yicha olinadi,
 * ikkinchisi uni to'g'rilaydi — yozgi vaqt chegarasida bir soat
 * adashmasin.
 */
function startOfDayIso(day: string, zone: string): string {
  const parts = dayParts(day)
  if (!parts) return day
  const wall = Date.UTC(parts[0], parts[1] - 1, parts[2], 0, 0, 0)
  const first = wall - zoneOffsetMinutes(zone, new Date(wall)) * 60000
  const exact = wall - zoneOffsetMinutes(zone, new Date(first)) * 60000
  return new Date(exact).toISOString()
}

export interface DayWindow {
  /** Davr boshi, KIRITILADI */
  from: string
  /** Davr oxiri, KIRITILMAYDI */
  to: string
}

/**
 * Bitta tenant kuni — `[boshi, ertangi kun boshi)`.
 *
 * ⚠️ Oxirgi chegara KIRITILMAYDI va bu server bilan kelishilgan:
 * `CommissionSummary.to` javadoc'i aynan shunday deydi. Uni kunning
 * oxirgi millisekundiga (`23:59:59.999`) aylantirish o'sha
 * millisekundda yozilgan amalni jimgina tashlab ketardi.
 */
export function tenantDayWindow(day: string, zone: string = getDisplayTimeZone()): DayWindow {
  return { from: startOfDayIso(day, zone), to: startOfDayIso(nextDay(day), zone) }
}

/**
 * Kalendar kunlari oralig'i — IKKALA CHETI HAM KIRITILADI.
 *
 * Bu ekrandagi `RangePicker` ning ma'nosi: kassir «1-dan 5-gacha» deb
 * tanlaganda 5-kun ham ichida. Onga aylantirishda esa oxirgi chegara
 * KIRITILMAYDIGAN bo'lishi kerak — shuning uchun `to` ning ERTASI
 * olinadi ({@link tenantDayWindow} bilan bir xil qaror).
 */
export interface DayRange {
  /** `YYYY-MM-DD`, kiritiladi */
  from: string
  /** `YYYY-MM-DD`, KIRITILADI */
  to: string
}

/**
 * Kunlar oralig'i → `[boshi, oxirgi kundan keyingi kun boshi)`, UTC ISO.
 *
 * ⚠️ TENANT ZONASIDA, {@link tenantDayWindow} bilan AYNAN bir xil yo'l.
 * `dayjs(...).startOf('day').toISOString()` bu yerda ham brauzer
 * zonasini olardi va Kobul tenantida (UTC+04:30) kuniga yarim soat
 * davomida oraliq bir kunga surilardi — JIMGINA, chunki so'rov baribir
 * 200 qaytaradi.
 *
 * ⚠️ Teskari oraliq (`to < from`) TUZATILMAYDI: bo'sh natija «bu davrda
 * hech narsa yo'q» degan yolg'on javob bo'lardi. Chetlar almashtiriladi,
 * chunki tanlagichdan bunday qiymat kelishi mumkin emas va kelsa ham
 * foydalanuvchi AYNAN shu ikki kunni ko'rsatgan.
 */
export function tenantRangeWindow(
  range: DayRange,
  zone: string = getDisplayTimeZone(),
): DayWindow {
  const [first, last] = range.from <= range.to ? [range.from, range.to] : [range.to, range.from]
  return { from: startOfDayIso(first, zone), to: startOfDayIso(nextDay(last), zone) }
}

/**
 * KIRITILMAYDIGAN davr oxiri → o'sha davrga KIRGAN oxirgi on.
 *
 * ⚠️ NEGA KERAK: server davrni `[from, to)` deb qaytaradi
 * (`CommissionSummary.to` javadoc'i) — ya'ni avgust oyi uchun `to` =
 * 1-SENTABR. Uni ekranda to'g'ridan-to'g'ri ko'rsatish «1-avgustdan
 * 1-sentabrgacha» degan qator berardi va sherik sentabrning birinchi
 * kuni ham ichida deb o'ylardi.
 *
 * ⚠️ BIR MILLISEKUND ayiriladi, «bir kun» EMAS: `to` har doim ham
 * yarim tunda bo'lavermaydi (foydalanuvchi ixtiyoriy oraliq tanlashi
 * mumkin), va bir kun ayirish o'sha holatda sanani noto'g'ri surardi.
 *
 * ⚠️ Zona bu yerga KIRMAYDI: natija baribir ISO on bo'lib qaytadi va
 * uni ekranga chizadigan `formatDate` zonani O'ZI qo'llaydi.
 * Shakli buzuq qiymat O'ZGARISHSIZ qaytadi — sana o'rniga bo'sh joy
 * chiqishidan ko'ra serverning qiymati ko'rsatilgani afzal.
 */
export function lastIncludedInstant(toExclusiveIso: string): string {
  const at = Date.parse(toExclusiveIso)
  if (Number.isNaN(at)) return toExclusiveIso
  return new Date(at - 1).toISOString()
}

/**
 * Tez tanlash — kalendar ochmasdan.
 *
 * ⚠️ HAMMASI `tenantToday()` DAN chiqadi, `new Date()` dan emas: «bugun»
 * tenant zonasidagi kun, kassirning brauzeridagi emas. Aks holda tugma
 * kuniga yarim soat davomida BOSHQA kunni tanlardi.
 */
export const QUICK_RANGES = ['today', 'yesterday', 'week', 'month'] as const
export type QuickRange = (typeof QUICK_RANGES)[number]

export function quickRange(kind: QuickRange, zone: string = getDisplayTimeZone()): DayRange {
  const today = tenantToday(zone)
  switch (kind) {
    case 'today':
      return { from: today, to: today }
    case 'yesterday': {
      const y = shiftDay(today, -1)
      return { from: y, to: y }
    }
    case 'week':
      // «7 kun» — bugun BILAN BIRGA yetti kun, ya'ni -6
      return { from: shiftDay(today, -6), to: today }
    case 'month':
      return { from: monthStart(today), to: today }
  }
}

/** Oraliq shu tez tanlash bilan bir xilmi — tugmani belgilash uchun */
export function isQuickRange(
  range: DayRange,
  kind: QuickRange,
  zone: string = getDisplayTimeZone(),
): boolean {
  const q = quickRange(kind, zone)
  return q.from === range.from && q.to === range.to
}
